// src/views/EstimatesView.js
// ============================================================================
// ESTIMADOS (Motor de Precio MVP · Fase 2) — cotización comprometida → producción.
//
// Lista los estimados (colección `estimates`, escrita SOLO por el servidor). Permite:
//  • Aprobar un descuento sobre el tope (permiso canApproveDiscounts → callable approveEstimate).
//  • Convertir un estimado aprobado en un Job de producción (canCreateJobs → convertEstimateToJob).
// "Cliente tonto": la app solo lee y dispara callables; el servidor recalcula y emite ITC.
// ============================================================================
import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { useNavigate } from 'react-router-dom';
import { buildSpecSheetPdf } from '../lib/specSheet';
import { parseDxf } from '../lib/dxfParse';
import { downloadPdfBase64 } from '../lib/estimatePdf.mjs';

const money = (n) => (typeof n === 'number' ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—');
const pct = (n) => (typeof n === 'number' ? `${Math.round(n * 100)}%` : '—');

// ── Fase 1 §4.2: comparador D1 — variables que mueven precio, lado a lado. Sin diff
//    visual de imposición/DXF (D2, diferido). Cada fila formatea la variable de una versión.
const D1_ROWS = [
  ['Material(es)', (v) => [...new Set((v.piezasResumen || []).map((p) => p.materialId).filter(Boolean))].join(', ') || '—'],
  ['Cantidad', (v) => (v.quantity != null ? String(v.quantity) : '—')],
  ['Tier', (v) => (v.tier != null ? String(v.tier) : '—')],
  ['Markup', (v) => (v.markup != null ? `${Math.round(v.markup * 100)}%` : '—')],
  ['Costo/un', (v) => (v.costPerUnit != null ? `$${Number(v.costPerUnit).toFixed(3)}` : '—')],
  ['Precio/un', (v) => (v.pricePerUnit != null ? `$${Number(v.pricePerUnit).toFixed(3)}` : '—')],
  ['Config de precios (rev)', (v) => (v.pricingConfigVersion ? `rev ${v.pricingConfigVersion.rev}` : '—')],
];

// Panel de versiones de UN borrador: chips seleccionables (máx 2 → diff), Duplicar por versión.
const VersionsPanel = ({ versions, sel, onToggle, onDuplicar }) => {
  if (!versions) return <p className="text-xs text-gray-400">Cargando versiones…</p>;
  if (!versions.length) return <p className="text-xs text-gray-400">Sin versiones guardadas.</p>;
  const selected = versions.filter((v) => sel.includes(v.vid));
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {versions.map((v) => (
          <div key={v.vid} className={`flex items-center gap-1.5 border rounded px-2 py-1 text-xs ${sel.includes(v.vid) ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 bg-white'}`}>
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={sel.includes(v.vid)} onChange={() => onToggle(v.vid)} />
              <b>{v.versionLabel || v.vid}</b>
            </label>
            <span className="text-gray-500">${Number(v.pricePerUnit || 0).toFixed(2)}/un · rev {v.pricingConfigVersion ? v.pricingConfigVersion.rev : '—'}</span>
            <button onClick={() => onDuplicar(v.vid)} title="Duplicar esta versión para ajustar (al guardar nace la siguiente)"
              className="ml-1 px-1.5 py-0.5 border rounded text-indigo-700 hover:bg-indigo-100">Duplicar</button>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400">Marca DOS versiones para compararlas lado a lado.</p>
      {selected.length === 2 && (
        <table className="text-sm border border-gray-200 rounded overflow-hidden">
          <thead className="bg-gray-100 text-xs uppercase">
            <tr><th className="px-3 py-1.5 text-left">Variable</th>
              {selected.map((v) => <th key={v.vid} className="px-3 py-1.5 text-center">{v.versionLabel}</th>)}</tr>
          </thead>
          <tbody className="divide-y">
            {D1_ROWS.map(([label, fmt]) => {
              const vals = selected.map(fmt);
              const difieren = vals[0] !== vals[1];   // el diff RESALTA por qué dos versiones difieren
              return (
                <tr key={label} className={difieren ? 'bg-amber-50' : ''}>
                  <td className="px-3 py-1.5 text-gray-500">{label}</td>
                  {vals.map((x, i) => <td key={i} className={`px-3 py-1.5 text-center tabular-nums ${difieren ? 'font-semibold text-amber-800' : ''}`}>{x}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// Botón "📄 PDF" con selector de idioma (opción b): UN solo control que al clic se expande
// INLINE a [Español | English | ✕] — sin dropdown absoluto (la tabla tiene overflow-auto y
// lo recortaría en las filas bajas). El spinner arranca al ELEGIR idioma, no al abrir el
// selector. Estado de carga y aislamiento POR FILA (busyId/menuId contra row.id).
const PdfLangButton = ({ row, busyId, menuId, onToggle, onPick }) => {
  if (busyId === row.id) {
    return (
      <button disabled
        className="px-2.5 py-1.5 text-xs font-medium text-gray-800 bg-gray-100 rounded disabled:opacity-60 disabled:cursor-wait">
        Generando PDF…
      </button>
    );
  }
  if (menuId === row.id) {
    return (
      <span className="inline-flex items-center gap-1">
        <button onClick={() => onPick(row, 'es')} title="Descargar PDF en español"
          className="px-2 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 rounded hover:bg-indigo-100">
          Español
        </button>
        <button onClick={() => onPick(row, 'en')} title="Download PDF in English"
          className="px-2 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 rounded hover:bg-indigo-100">
          English
        </button>
        <button onClick={() => onToggle(null)} title="Cancelar"
          className="px-1 py-1.5 text-xs text-gray-400 hover:text-gray-600">✕</button>
      </span>
    );
  }
  return (
    <button onClick={() => onToggle(row.id)} title="Descargar PDF del estimado (elige idioma)"
      className="px-2.5 py-1.5 text-xs font-medium text-gray-800 bg-gray-100 rounded hover:bg-gray-200">
      📄 PDF
    </button>
  );
};

const STATUS_BADGE = {
  pending_approval: { label: 'Pendiente de aprobación', cls: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Aprobado', cls: 'bg-green-100 text-green-800' },
  converted: { label: 'Convertido a Job', cls: 'bg-blue-100 text-blue-800' },
  rejected: { label: 'Rechazado', cls: 'bg-red-100 text-red-800' },
};

const EstimatesView = ({ db, user }) => {
  const functions = useMemo(() => getFunctions(getApp(), 'us-central1'), []);
  const navigate = useNavigate();

  const [estimates, setEstimates] = useState([]);
  // Fase 1 Slice 4: borradores (draft/versioned) — "En curso", separados de los formales.
  const [enCurso, setEnCurso] = useState([]);
  const [versionsBy, setVersionsBy] = useState({});   // draftId -> [versiones] (carga bajo demanda)
  const [openDraft, setOpenDraft] = useState(null);   // draftId expandido (comparador)
  const [compareSel, setCompareSel] = useState([]);   // etiquetas de versión seleccionadas (máx 2)
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  // Descargar PDF por fila (CF renderEstimatePdf): carga AISLADA por fila — el cold-start
  // real es ~12.5s y el boton se bloquea para impedir dobles renders.
  const [pdfBusyId, setPdfBusyId] = useState(null);
  // Selector de idioma por descarga (opción b): fila con el selector ES/EN desplegado.
  const [pdfMenuId, setPdfMenuId] = useState(null);

  const canApprove = !!user.permissions?.canApproveDiscounts;
  const canCreateJobs = !!user.permissions?.canCreateJobs;
  // Eliminar estimado: acción destructiva, gobernada por canManagePricing (gestión comercial,
  // misma que el Configurador). El servidor revalida el permiso en la CF deleteEstimate.
  const canDelete = !!user.permissions?.canManagePricing;

  const load = async () => {
    setIsLoading(true);
    try {
      const snap = await getDocs(collection(db, 'estimates'));
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Fase 1 (Slice 4): los borradores (draft/versioned) van al listado "En curso";
      // la tabla principal queda solo con los formales (approved/converted/...).
      const esDraft = (x) => x.status === 'draft' || x.status === 'versioned';
      setEnCurso(all.filter(esDraft)
        .sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0)));
      setEstimates(all.filter((x) => !esDraft(x))
        .sort((a, b) => (b.estimateNumber || 0) - (a.estimateNumber || 0)));
    } catch (e) {
      console.error('Error cargando estimados:', e);
      setError('No se pudieron cargar los estimados.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, [db]);

  const downloadSpecSheet = async (e) => {
    try {
      await buildSpecSheetPdf({
        refId: e.estimateCode || e.id,
        clientName: e.clientName || e.clientId,
        jobName: e.jobName,
        deliveryDate: e.deliveryDate,
        createdBy: e.createdBy,
        pieces: (e.pieces || []).map((p, i) => ({
          materialName: p.materialName,
          quantity: p.quantity,
          ups: p.ups,
          ruleLengthPerSheet: p.ruleLengthPerSheet,
          geometry: p.geometry,
          // Slice 1 sourcing (bug 1): la FORMA vive en el intake crudo que createEstimate
          // ahora persiste — sin él, el spec desde Estimados salía sin dibujo.
          polys: e.intake && e.intake.pieces && e.intake.pieces[i] && e.intake.pieces[i].dxf
            ? parseDxf(e.intake.pieces[i].dxf) : [],
        })),
        output: 'save',
      });
    } catch (err) {
      console.error('Spec sheet falló:', err);
      setError('No se pudo generar el spec sheet.');
    }
  };

  // ── Fase 1 Slice 4: expandir un borrador → carga sus versiones (subcolección, bajo demanda).
  const toggleDraft = async (id) => {
    if (openDraft === id) { setOpenDraft(null); setCompareSel([]); return; }
    setOpenDraft(id); setCompareSel([]);
    if (!versionsBy[id]) {
      try {
        const snap = await getDocs(collection(db, 'estimates', id, 'versions'));
        const vs = snap.docs.map((d) => ({ vid: d.id, ...d.data() }))
          .sort((a, b) => (a.versionNumber || 0) - (b.versionNumber || 0));
        setVersionsBy((m) => ({ ...m, [id]: vs }));
      } catch (e) { setError('No se pudieron cargar las versiones del borrador.'); }
    }
  };
  // Selección para comparar: máximo 2 (la nueva desplaza a la más vieja).
  const toggleCompare = (vid) => setCompareSel((sel) => (sel.includes(vid)
    ? sel.filter((x) => x !== vid)
    : [...sel.slice(-1), vid]));

  // Descarga el PDF de UNA fila en el idioma ELEGIDO (opción b: el usuario decide por
  // descarga; ya no manda el descriptionLang del doc). Mismo par CF+helper que
  // ProductQuoteView/QuoteView. Si falla, restaura el botón y avisa (nunca queda colgado).
  const downloadPdf = async (e, lang) => {
    setPdfMenuId(null);                    // el selector se cierra al elegir; AHÍ arranca el spinner
    setError(''); setPdfBusyId(e.id);
    try {
      const fn = httpsCallable(functions, 'renderEstimatePdf');
      const res = await fn({ estimateId: e.id, lang });
      downloadPdfBase64(res.data.pdfBase64, `${res.data.estimateCode || 'estimate'}-${res.data.lang}.pdf`);
    } catch (err) {
      console.error('renderEstimatePdf falló:', err);
      setError(err.message || 'No se pudo generar el PDF del estimado.');
    } finally {
      setPdfBusyId(null);
    }
  };

  const runAction = async (name, estimateId, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setError('');
    setBusyId(estimateId);
    try {
      const fn = httpsCallable(functions, name);
      const res = await fn({ estimateId });
      if (name === 'convertEstimateToJob' && res.data?.jobId) {
        await load();
        navigate(`/jobs/${res.data.jobId}`);
        return;
      }
      await load();
    } catch (e) {
      console.error(`${name} falló:`, e);
      setError(e.message || `No se pudo ejecutar ${name}.`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-4 md:p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Estimados</h1>
          <p className="text-sm text-gray-500">Cotizaciones comprometidas con cliente. Aprobá descuentos y convertí a Job de producción.</p>
        </div>
        <button onClick={load} className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">Actualizar</button>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {/* ── EN CURSO (Fase 1 §4.2): borradores draft/versioned, SEPARADOS de los formales.
          Retomar carga la última versión en el cotizador; Duplicar parte de una vN puntual;
          Comparar muestra el diff D1. Los borradores no tienen número YY-NNN. ── */}
      {enCurso.length > 0 && (
        <div className="bg-white rounded-xl shadow mb-4 overflow-auto">
          <div className="px-4 py-3 border-b flex items-baseline gap-2">
            <h2 className="text-base font-bold text-gray-800">En curso</h2>
            <span className="text-xs text-gray-400">borradores sin número — retoma, versiona y compara antes de formalizar</span>
          </div>
          <table className="w-full text-sm text-left text-gray-600">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              <tr>
                <th className="px-4 py-2">Trabajo</th>
                <th className="px-4 py-2">Cliente</th>
                <th className="px-4 py-2 text-center">Versiones</th>
                <th className="px-4 py-2 text-center">Estado</th>
                <th className="px-4 py-2 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {enCurso.map((d) => (
                <React.Fragment key={d.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-900">{d.jobName || '—'}</td>
                    <td className="px-4 py-2">{d.clientName || d.clientId || '—'}</td>
                    <td className="px-4 py-2 text-center">{d.versionCount || 0}</td>
                    <td className="px-4 py-2 text-center">
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                        {d.status === 'versioned' ? `versionado (v${d.currentVersion})` : 'borrador'}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-center gap-2">
                        <button onClick={() => navigate(`/quote-product?draft=${d.id}`)}
                          className="px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700">
                          Retomar
                        </button>
                        {/* PDF con selector de idioma por descarga (carga aislada por fila). */}
                        <PdfLangButton row={d} busyId={pdfBusyId} menuId={pdfMenuId}
                          onToggle={setPdfMenuId} onPick={downloadPdf} />
                        {(d.versionCount || 0) > 0 && (
                          <button onClick={() => toggleDraft(d.id)}
                            className="px-2.5 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 rounded hover:bg-indigo-100">
                            {openDraft === d.id ? 'Cerrar' : 'Versiones / Comparar'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {openDraft === d.id && (
                    <tr>
                      <td colSpan={5} className="px-4 py-3 bg-gray-50/60">
                        <VersionsPanel versions={versionsBy[d.id]} sel={compareSel} onToggle={toggleCompare}
                          onDuplicar={(vid) => navigate(`/quote-product?draft=${d.id}&version=${vid}`)} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex-grow bg-white rounded-xl shadow overflow-auto">
        {isLoading ? (
          <div className="text-center p-10 text-gray-400">Cargando…</div>
        ) : estimates.length === 0 ? (
          <div className="text-center p-10 text-gray-400">
            <p className="text-lg">Todavía no hay estimados.</p>
            <p className="text-sm">Crea uno desde el <button onClick={() => navigate('/quote-product')} className="text-blue-600 underline">Cotizador</button>.</p>
          </div>
        ) : (
          <table className="w-full text-sm text-left text-gray-600">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3">Estimado</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Trabajo</th>
                <th className="px-4 py-3 text-center">Total</th>
                <th className="px-4 py-3 text-center">$/un</th>
                <th className="px-4 py-3 text-center">Descuento</th>
                <th className="px-4 py-3 text-center">Estado</th>
                <th className="px-4 py-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {estimates.map((e) => {
                const badge = STATUS_BADGE[e.status] || { label: e.status, cls: 'bg-gray-100 text-gray-700' };
                const d = e.discount || {};
                const busy = busyId === e.id;
                // Fase 1 (Ajuste 1): el código YY-NNN vive en `estimateCode`; en estimados
                // viejos el docId ERA el código → fallback. Acciones siguen usando e.id (docId).
                const code = e.estimateCode || e.id;
                return (
                  <tr key={e.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono font-medium text-gray-900">{code}</td>
                    <td className="px-4 py-3">{e.clientName || e.clientId || '—'}</td>
                    <td className="px-4 py-3">{e.jobName}</td>
                    {/* Cost-real: la verdad es precioVenta/precioVentaUnit. Fallback legacy sin recomputar. */}
                    <td className="px-4 py-3 text-center font-medium">{money(e.precioVenta ?? e.pricing?.total)}</td>
                    <td className="px-4 py-3 text-center">{money(e.precioVentaUnit ?? e.pricing?.unit)}</td>
                    <td className="px-4 py-3 text-center">
                      {/* El modelo cost-real no tiene descuento → celda vacía (no "—"). */}
                      {e.discount ? (
                        <>
                          {pct(d.applied)}
                          {d.needsApproval && !d.approved && (
                            <span className="block text-xs text-amber-600">pide {pct(d.requested)}</span>
                          )}
                        </>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center items-center gap-2">
                        <button onClick={() => downloadSpecSheet(e)} title="Descargar spec sheet (PDF)"
                          className="px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 rounded hover:bg-indigo-100">
                          Spec sheet
                        </button>
                        {/* PDF del estimado (cliente): selector de idioma POR DESCARGA (opción b).
                            Carga AISLADA por fila: bloquea solo este control durante el render. */}
                        <PdfLangButton row={e} busyId={pdfBusyId} menuId={pdfMenuId}
                          onToggle={setPdfMenuId} onPick={downloadPdf} />
                        {e.status === 'pending_approval' && canApprove && (
                          <button disabled={busy}
                            onClick={() => runAction('approveEstimate', e.id, `Aprobar el descuento de ${pct(d.requested)} en ${code}?`)}
                            className="px-2.5 py-1.5 text-xs font-medium text-white bg-amber-600 rounded hover:bg-amber-700 disabled:opacity-60">
                            {busy ? '…' : 'Aprobar descuento'}
                          </button>
                        )}
                        {e.status === 'approved' && canCreateJobs && (
                          <button disabled={busy}
                            onClick={() => runAction('convertEstimateToJob', e.id, `Convertir ${code} en un Job de producción?`)}
                            className="px-2.5 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-60">
                            {busy ? '…' : 'Convertir a Job'}
                          </button>
                        )}
                        {e.status === 'converted' && e.jobId && (
                          <button onClick={() => navigate(`/jobs/${e.jobId}`)} className="text-blue-600 underline text-xs">{e.jobId}</button>
                        )}
                        {e.status === 'pending_approval' && !canApprove && (
                          <span className="text-xs text-gray-400">esperando aprobación</span>
                        )}
                        {/* Eliminar: no para convertidos (un Job los referencia → la CF también lo bloquea). */}
                        {canDelete && e.status !== 'converted' && (
                          <button disabled={busy} title="Eliminar estimado"
                            onClick={() => runAction('deleteEstimate', e.id, `¿Eliminar el estimado ${code} de ${e.clientName || e.clientId || 'cliente'}? Esta acción no se puede deshacer.`)}
                            className="px-2.5 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-60">
                            {busy ? '…' : 'Eliminar'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default EstimatesView;
